import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import Modal from './ui/Modal';
import { supabase } from '../services/supabase';
import { registrarIntentoFallido } from '../services/borradoCuenta';

// El diálogo de borrado definitivo.
//
// Sustituye a un `Confirm` genérico de "¿seguro?" que se pulsaba sin leer, y
// que además mentía: el borrado que lanzaba no borraba a casi nadie —la cascada
// a `legal_acceptances` chocaba con su trigger append-only y abortaba— pero la
// fila desaparecía de la lista igualmente porque el error se descartaba.
//
// Tres cosas que este componente hace y el anterior no:
//   1. ENSEÑA lo que va a pasar antes de que pase. El admin no puede saber de
//      memoria qué cuelga de cada ficha.
//   2. Exige teclear el nombre completo. Incómodo a propósito.
//   3. Comprueba los RECUENTOS que devuelve el servidor, no el error. PostgREST
//      responde 204 aunque no se toque ninguna fila: un `error === null` no
//      prueba absolutamente nada.
//   4. Deja RASTRO del intento cuando falla (mig 287). El asiento del borrado
//      bueno va dentro de la transacción del borrado, así que un borrado que
//      aborta no deja nada: «no se intentó» y «se intentó y falló» se leían
//      igual, y con el BUG 14 hubo que reproducir el fallo para distinguirlos.

interface Props {
  open: boolean;
  /** La cuenta a borrar. Un alumno o —desde el BUG 14— un tutor. */
  cuenta: { id: string; full_name: string } | null;
  onClose: () => void;
  onDeleted: (id: string) => void;
  /**
   * Solo para tutores: lo que hay que decir de los hijos y del vínculo, que el
   * recuento genérico no cubre. `inscripciones` y `evaluaciones` de un tutor
   * son siempre 0, y lo que de verdad le pasa a su familia no se ve en ninguna
   * de las dos listas de arriba.
   */
  tutor?: { hijosVivos: number; vinculosTotales: number };
}

interface Rastro {
  pedidos: number;
  bonos: number;
  manuales: number;
  inscripciones: number;
  videos: number;
}

// El HTML colapsa los espacios repetidos al renderizar, así que un nombre como
// 'Victor  Moreno' se LEE 'Victor Moreno' y nadie puede teclear lo que no ve.
// Se compara colapsado en los dos lados — igual que hace el servidor, que es
// quien manda. Tildes y mayúsculas siguen siendo obligatorias.
function colapsar(s: string): string {
  return s.trim().replace(/\s+/g, ' ');
}

export default function EliminarAlumnoModal({ open, cuenta, onClose, onDeleted, tutor }: Props) {
  const [nombre, setNombre] = useState('');
  const [rastro, setRastro] = useState<Rastro | null>(null);
  const [cargando, setCargando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState('');
  const [sinConstancia, setSinConstancia] = useState(false);

  // La traza se pide sin esperarla, así que puede volver con el diálogo ya
  // cerrado. Sin esto, el `setState` caería sobre un componente desmontado.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open || !cuenta) return;
    setNombre('');
    setError('');
    setSinConstancia(false);
    setRastro(null);
    setCargando(true);

    (async () => {
      const uid = cuenta.id;
      // Se llamaba `cuenta`, como el verbo. Ahora `cuenta` es la cuenta a
      // borrar, así que el contador pasa a `contar` y deja de haber dos cosas
      // con el mismo nombre en el mismo ámbito.
      // ⚠️ AQUÍ UN RECUENTO FALLIDO ABRÍA EL BORRADO.
      //
      // `return count ?? 0` convertía un error en un cero, y de ese cero
      // cuelgan las dos protecciones de este modal:
      //
      //   · `conVideos = (rastro?.videos ?? 0) > 0` deshabilita el botón
      //     cuando la cuenta tiene vídeos en R2, que el borrado de aquí no
      //     puede limpiar. Con la lectura caída, `videos` valía 0, `conVideos`
      //     era false y **el botón quedaba habilitado**.
      //   · El resumen decía «Se conserva… 0 pedidos, 0 bonos… Se borra… 0
      //     inscripciones», o sea que se borraba una cuenta con la pantalla
      //     afirmando que no cuelga nada de ella.
      //
      // Y el `try/catch` que había debajo era código muerto: postgrest-js no
      // rechaza nunca, devuelve `{ data: null, error }`. El mensaje correcto
      // —«No se pudo consultar qué cuelga de esta cuenta. No se borra nada.»—
      // estaba escrito y no se ejecutaba jamás. Ahora sí.
      const contar = async (tabla: string, col: string): Promise<number | null> => {
        const { count, error } = await supabase
          .from(tabla)
          .select('id', { count: 'exact', head: true })
          .eq(col, uid);
        if (error) return null;
        return count ?? 0;
      };
      const [pedidos, bonos, manuales, inscripciones, videos] = await Promise.all([
        contar('orders', 'user_id'),
        contar('user_credits', 'user_id'),
        contar('manual_bono_activations', 'student_id'),
        contar('enrollments', 'user_id'),
        contar('gym_videos', 'user_id'),
      ]);
      if (
        pedidos === null ||
        bonos === null ||
        manuales === null ||
        inscripciones === null ||
        videos === null
      ) {
        setError('No se pudo consultar qué cuelga de esta cuenta. No se borra nada.');
      } else {
        setRastro({ pedidos, bonos, manuales, inscripciones, videos });
      }
      setCargando(false);
    })();
  }, [open, cuenta]);

  if (!cuenta) return null;

  const coincide = colapsar(nombre) === colapsar(cuenta.full_name) && nombre.trim() !== '';
  const conVideos = (rastro?.videos ?? 0) > 0;
  // Sin rastro no se sabe qué cuelga, y sin saberlo no se borra. Es la
  // dirección segura: antes el desconocimiento se leía como «no cuelga nada».
  const sinRastro = rastro === null;

  // El orden importa y es el del acuerdo: PRIMERO el error en pantalla, la
  // traza después y sin bloquear. Si la traza no llega, se dice en voz baja —
  // pero se dice: una traza muda sería el mismo fallo una capa más arriba.
  function dejarConstancia(targetId: string, sqlstate: string | null, mensaje: string) {
    void registrarIntentoFallido(targetId, sqlstate, mensaje).then((quedo) => {
      if (!quedo && montado.current) setSinConstancia(true);
    });
  }

  async function borrar() {
    if (!cuenta || !coincide) return;
    setBorrando(true);
    setError('');

    const { data, error: rpcError } = await supabase.rpc('admin_delete_account', {
      p_target: cuenta.id,
      p_confirm_name: nombre,
    });

    setBorrando(false);

    if (rpcError) {
      // [356] El invariante de la familia habla en SQL y dirección no tiene por
      // qué leer eso. El código es `23514` (check_violation) y el mensaje del
      // servidor nombra la operación que sí aplica; aquí se traduce UNA vez,
      // sin duplicar la regla en el cliente: la autoridad sigue siendo la base.
      const esMenorSinTutor =
        rpcError.code === '23514' && /menor sin tutor|ficha de tutor MUERTA/i.test(rpcError.message ?? '');
      setError(
        esMenorSinTutor
          ? 'No se puede borrar: le queda al menos un hijo MENOR vinculado y un menor no puede quedarse sin tutor. La baja de esa familia la pide el tutor desde su app, y se lleva también la cuenta del niño.'
          : rpcError.message || 'No se pudo borrar la cuenta.'
      );
      // TODOS los fallos, incluido el nombre mal tecleado y la cuenta sin
      // desactivar: los dos comparten SQLSTATE con el resto, así que se guarda
      // el mensaje y se filtra al leer. Un intento con el nombre mal escrito
      // sigue siendo «se intentó y falló», que es lo que faltaba saber.
      dejarConstancia(cuenta.id, rpcError.code ?? null, rpcError.message ?? '');
      return;
    }

    // ⚠️ NO basta con que no haya error. Se exige el recuento.
    const res = data as { ok?: boolean; profiles_deleted?: number } | null;
    if (!res?.ok || res.profiles_deleted !== 1) {
      setError(
        'El servidor no confirmó el borrado. La cuenta puede seguir existiendo: recarga y compruébalo.'
      );
      // Sin excepción no hay `code`: aquí el fallo es el recuento, y eso es lo
      // que tiene que quedar escrito.
      dejarConstancia(
        cuenta.id,
        null,
        `el servidor no confirmo el borrado (ok=${String(res?.ok)}, profiles_deleted=${String(
          res?.profiles_deleted
        )})`
      );
      return;
    }

    onDeleted(cuenta.id);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="Eliminar definitivamente" size="md">
      <div className="space-y-4 font-inter text-sm">
        <div className="flex gap-3 rounded-lg border border-error/40 bg-error/10 p-3">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-error" />
          <p className="text-text-primary">
            Se borrará la cuenta de <strong>{cuenta.full_name}</strong> por completo: su perfil, su
            acceso y sus consentimientos. <strong>No se puede deshacer.</strong> Su email quedará
            libre y podrá volver a usarse.
          </p>
        </div>

        {cargando && (
          <p className="flex items-center gap-2 text-text-secondary">
            <Loader2 size={14} className="animate-spin" /> Comprobando qué cuelga de esta cuenta…
          </p>
        )}

        {rastro && (
          <div className="space-y-2">
            <p className="text-text-secondary">Qué pasa con lo que tiene:</p>
            <ul className="space-y-1 text-text-primary" data-testid="eliminar-rastro">
              <li>
                <strong>Se conserva</strong> el rastro contable con su nombre y el detalle de lo
                comprado: {rastro.pedidos} pedido{rastro.pedidos !== 1 ? 's' : ''}, {rastro.bonos}{' '}
                bono{rastro.bonos !== 1 ? 's' : ''}, {rastro.manuales} activación
                {rastro.manuales !== 1 ? 'es' : ''} manual{rastro.manuales !== 1 ? 'es' : ''}.
              </li>
              <li>
                <strong>Se borra</strong> todo lo demás: {rastro.inscripciones} inscripción
                {rastro.inscripciones !== 1 ? 'es' : ''}, evaluaciones, mensajes, notificaciones y
                datos personales.
              </li>
            </ul>
          </div>
        )}

        {/* BUG 14 · lo que el recuento genérico no dice de un tutor: qué pasa
            con sus hijos y con el vínculo. Sin esto, dirección lee "se borra
            todo lo demás" y no sabe si se lleva a los niños por delante. */}
        {tutor && (
          <div
            className="rounded-lg border border-bg-border bg-bg-subtle p-3"
            data-testid="eliminar-nota-tutor"
          >
            <p className="text-text-primary">
              {tutor.hijosVivos === 0
                ? 'No le queda ningún hijo vinculado.'
                : `Le quedan ${tutor.hijosVivos} hijo${
                    tutor.hijosVivos === 1 ? '' : 's'
                  } vinculado${tutor.hijosVivos === 1 ? '' : 's'}. Si alguno es MENOR, este borrado no se puede hacer: la base lo rechaza, porque un menor no puede quedarse sin tutor. En ese caso la baja es de la familia entera y la pide el propio tutor desde la app.`}{' '}
              {tutor.vinculosTotales === 0
                ? 'Su ficha de tutor se borra también: no le queda ningún vínculo, ni vivo ni retirado.'
                : 'Su ficha de tutor se conserva como ancla del historial, pero queda marcada como sin persona detrás y SALE del censo de familias: ya no volverá a la lista de pendientes.'}
            </p>
          </div>
        )}

        {conVideos && (
          <p className="rounded-lg border border-error/40 bg-error/10 p-3 text-error">
            Esta cuenta tiene {rastro?.videos} vídeo(s) en R2. El borrado desde aquí no puede
            limpiarlos y el servidor lo va a rechazar: hay que usar el borrado con limpieza de R2.
          </p>
        )}

        <div>
          <label className="mb-1 block text-text-secondary" htmlFor="confirm-nombre">
            Escribe el nombre completo del cuenta para confirmar:
          </label>
          <input
            id="confirm-nombre"
            data-testid="eliminar-confirm-nombre"
            className="input w-full"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            autoComplete="off"
            placeholder={cuenta.full_name}
          />
        </div>

        {error && (
          <p
            className="rounded-lg border border-error/40 bg-error/10 p-3 text-error"
            role="alert"
            data-testid="eliminar-error"
          >
            {error}
          </p>
        )}

        {sinConstancia && (
          <p className="text-xs text-text-secondary" data-testid="eliminar-sin-constancia">
            No se pudo dejar constancia del intento en el registro. El error de arriba es el que
            manda.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose} disabled={borrando}>
            Cancelar
          </button>
          <button
            className="btn-danger"
            data-testid="eliminar-confirmar"
            onClick={borrar}
            disabled={!coincide || borrando || cargando || conVideos || sinRastro}
          >
            {borrando ? 'Borrando…' : 'Eliminar definitivamente'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
